import React, { useState, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import {
  Download, Upload, Shield, AlertTriangle, CheckCircle2,
  Loader, Database, FolderOpen, Clock,
} from 'lucide-react';

export default function BackupPage() {
  const { user }     = useAuth();
  const { showToast } = useToast();
  const isAdmin      = user?.isAdmin === true;

  const [downloading, setDownloading]     = useState(false);
  const [restoring, setRestoring]         = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [restoreError, setRestoreError]   = useState(null);
  const [confirmOpen, setConfirmOpen]     = useState(false);
  const [pendingFile, setPendingFile]     = useState(null);
  const [restoreMode, setRestoreMode]     = useState('replace'); // 'replace' | 'merge'

  const fileInputRef = useRef(null);

  // ── Download backup ────────────────────────────────────────────────────────
  async function handleDownload() {
    setDownloading(true);
    try {
      // Use the correct session storage key that the app uses for auth tokens
      const token = sessionStorage.getItem('dq_token');
      const res   = await fetch('/api/backup/download', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Server returned ${res.status}`);
      }
      // Extract filename from Content-Disposition header
      const cd   = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      const name  = match ? match[1] : `dental-backup-${Date.now()}.zip`;

      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);

      showToast(`Backup downloaded: ${name}`, 'success');
    } catch (err) {
      showToast('Backup download failed: ' + err.message, 'error');
    } finally {
      setDownloading(false);
    }
  }

  // ── Restore — file pick → confirm → upload ─────────────────────────────────
  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.zip')) {
      showToast('Please select a .zip backup file.', 'error');
      return;
    }
    setPendingFile(file);
    setConfirmOpen(true);
    // reset the input so the same file can be re-selected if needed
    e.target.value = '';
  }

  async function handleConfirmRestore() {
    if (!pendingFile) return;
    setConfirmOpen(false);
    setRestoring(true);
    setRestoreResult(null);
    setRestoreError(null);

    try {
      const fd = new FormData();
      fd.append('backup', pendingFile);
      fd.append('mode', restoreMode); // 'replace' or 'merge'

      const res = await api.post('/backup/restore', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });

      setRestoreResult(res.data);
      showToast('Restore completed successfully.', 'success');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Restore failed.';
      setRestoreError(msg);
      showToast('Restore failed: ' + msg, 'error');
    } finally {
      setRestoring(false);
      setPendingFile(null);
    }
  }

  // Non-admin guard
  if (!isAdmin) {
    return (
      <AppLayout title="Backup & Restore" subtitle="Database and file backup management">
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--color-ink-faint)' }}>
          <Shield size={40} style={{ marginBottom: 14, opacity: 0.5 }} />
          <p style={{ fontWeight: 700, fontSize: 15, margin: '0 0 6px' }}>Admin access required</p>
          <p style={{ fontSize: 13, margin: 0 }}>Only the clinic administrator (main doctor) can access backup and restore.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Backup & Restore" subtitle="Download a full backup or restore from a previous backup">

      {/* Info banner */}
      <div style={{ display: 'flex', gap: 12, padding: '14px 18px', borderRadius: 10, background: 'var(--color-primary-soft)', border: '1px solid var(--color-primary)', marginBottom: 24, alignItems: 'flex-start' }}>
        <Shield size={20} color="var(--color-primary)" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 13, color: 'var(--color-primary)', lineHeight: 1.6 }}>
          <strong>What's included in a backup:</strong> all patient records, billing, pharmacy, lab orders, treatment catalog, staff accounts, and all uploaded files (PDFs and images attached to patient records).
          Store your backup file safely — it contains all clinic data.
        </div>
      </div>

      <div className="backup-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

        {/* ── Download card ────────────────────────────────────────── */}
        <div className="card" style={{ padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--color-success-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Download size={20} color="var(--color-success)" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Download Backup</div>
              <div style={{ fontSize: 12, color: 'var(--color-ink-faint)' }}>Export all data as a .zip file</div>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 10, marginBottom: 20, fontSize: 13, color: 'var(--color-ink-soft)' }}>
            <BulletRow icon={Database}   text="All patient records, billing, lab &amp; pharmacy data" />
            <BulletRow icon={FolderOpen} text="All uploaded images and PDF attachments" />
            <BulletRow icon={Clock}      text="Timestamp in filename — keep multiple versions" />
          </div>

          <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleDownload} disabled={downloading}>
            {downloading
              ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Preparing backup…</>
              : <><Download size={16} /> Download Backup</>}
          </button>
          {downloading && (
            <div style={{ fontSize: 12, color: 'var(--color-ink-faint)', marginTop: 8, textAlign: 'center' }}>
              Exporting all collections and files — this may take a moment…
            </div>
          )}
        </div>

        {/* ── Restore card ─────────────────────────────────────────── */}
        <div className="card" style={{ padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--color-warning-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Upload size={20} color="var(--color-warning)" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Restore from Backup</div>
              <div style={{ fontSize: 12, color: 'var(--color-ink-faint)' }}>Replace all data from a .zip backup file</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'var(--color-danger-soft)', border: '1px solid var(--color-danger)', marginBottom: 16, alignItems: 'flex-start' }}>
            <AlertTriangle size={16} color="var(--color-danger)" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 12, color: 'var(--color-danger)', lineHeight: 1.5 }}>
              <strong>Warning:</strong> Restore replaces ALL existing data with the contents of the backup. This cannot be undone. Download a current backup first if you want to preserve your current data.
            </div>
          </div>

          <input ref={fileInputRef} type="file" accept=".zip" style={{ display: 'none' }} onChange={handleFileChange} />
          <button className="btn" style={{ width: '100%', background: 'var(--color-warning)', color: 'white' }}
            onClick={() => fileInputRef.current?.click()} disabled={restoring}>
            {restoring
              ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Restoring…</>
              : <><Upload size={16} /> Select Backup File to Restore</>}
          </button>
          {restoring && (
            <div style={{ fontSize: 12, color: 'var(--color-ink-faint)', marginTop: 8, textAlign: 'center' }}>
              Importing all data and files — do not close this window…
            </div>
          )}

          {/* Error */}
          {restoreError && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--color-danger-soft)', borderRadius: 8, fontSize: 13, color: 'var(--color-danger)' }}>
              <strong>Restore failed:</strong> {restoreError}
            </div>
          )}

          {/* Success result */}
          {restoreResult && (
            <div style={{ marginTop: 14, padding: '14px 16px', background: 'var(--color-success-soft)', borderRadius: 8, fontSize: 13, color: 'var(--color-success)' }}>
              <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <CheckCircle2 size={15} /> Restore successful
              </div>
              <div style={{ color: 'var(--color-ink-soft)', marginBottom: 8, fontSize: 12 }}>
                Backup created: {restoreResult.backupCreatedAt ? new Date(restoreResult.backupCreatedAt).toLocaleString('en-IN') : '—'}
                {' · '}{restoreResult.filesRestored} file(s) restored
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                {(restoreResult.collections || []).map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-ink-soft)' }}>{c.collection}</span>
                    <span style={{ color: 'var(--color-success)' }}>{c.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Confirm restore dialog ─────────────────────────────────── */}
      {confirmOpen && (
        <div className="modal-overlay" onClick={() => { setConfirmOpen(false); setPendingFile(null); }}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '24px 24px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <AlertTriangle size={24} color="var(--color-warning)" />
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Restore Options</h3>
              </div>
              <div style={{ padding: '8px 12px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontFamily: 'var(--font-mono)', fontSize: 12, marginBottom: 16, wordBreak: 'break-all' }}>
                {pendingFile?.name}
              </div>
              {/* Mode cards */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div onClick={() => setRestoreMode('replace')}
                  style={{ padding: '14px 14px', borderRadius: 10, border: `2px solid ${restoreMode === 'replace' ? 'var(--color-danger)' : 'var(--color-border)'}`, background: restoreMode === 'replace' ? 'var(--color-danger-soft)' : 'var(--color-surface)', cursor: 'pointer' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: restoreMode === 'replace' ? 'var(--color-danger)' : 'var(--color-ink)', marginBottom: 5 }}>
                    🔄 Replace All Data
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', lineHeight: 1.5 }}>
                    Wipes all current data and replaces with backup. Use after data loss or corruption.
                  </div>
                </div>
                <div onClick={() => setRestoreMode('merge')}
                  style={{ padding: '14px 14px', borderRadius: 10, border: `2px solid ${restoreMode === 'merge' ? 'var(--color-primary)' : 'var(--color-border)'}`, background: restoreMode === 'merge' ? 'var(--color-primary-soft)' : 'var(--color-surface)', cursor: 'pointer' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: restoreMode === 'merge' ? 'var(--color-primary)' : 'var(--color-ink)', marginBottom: 5 }}>
                    🔀 Merge with Existing
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', lineHeight: 1.5 }}>
                    Adds backup records to current data. Existing records are kept. Use to recover missing records.
                  </div>
                </div>
              </div>
              {restoreMode === 'replace' && (
                <div style={{ padding: '9px 12px', background: 'var(--color-danger-soft)', borderRadius: 8, fontSize: 12, color: 'var(--color-danger)', lineHeight: 1.5, marginBottom: 14 }}>
                  <strong>⚠️ Warning:</strong> This permanently replaces ALL current data. Download a fresh backup first to save current data.
                </div>
              )}
              {restoreMode === 'merge' && (
                <div style={{ padding: '9px 12px', background: 'var(--color-primary-soft)', borderRadius: 8, fontSize: 12, color: 'var(--color-primary)', lineHeight: 1.5, marginBottom: 14 }}>
                  <strong>ℹ️ Note:</strong> Records already in the database (same ID) will be skipped. Only new records from the backup are added.
                </div>
              )}
            </div>
            <div style={{ padding: '0 24px 24px', display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1, background: restoreMode === 'replace' ? 'var(--color-danger)' : 'var(--color-primary)', color: 'white' }}
                onClick={handleConfirmRestore}>
                <Upload size={14} />
                {restoreMode === 'replace' ? 'Replace & Restore' : 'Merge & Restore'}
              </button>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => { setConfirmOpen(false); setPendingFile(null); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </AppLayout>
  );
}

function BulletRow({ icon: Icon, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Icon size={14} color="var(--color-success)" style={{ flexShrink: 0 }} />
      <span dangerouslySetInnerHTML={{ __html: text }} />
    </div>
  );
}
